import React, { useCallback, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ChatAssistance, { generateInteractionId } from '../ChatAssistance';
import { AssistantPanelContext } from '../assistantPanelContext';
import { dsVar } from '../../theme/tokens';

const AGENT_CODE = 'QUERY_POLICY_GRAPH_AND_RESPOND';
const PANEL_WIDTH = 400;

/**
 * Wraps every /ontology route and docks the Knowledge Assistant beside them.
 *
 * It lives here rather than in each page because the conversation belongs to the visit,
 * not to a page: this element stays mounted while the user moves between the landing
 * page, a tab, search and a record, so the messages and the interactionId carry across.
 * Leaving /ontology unmounts it, and the next visit starts a new session.
 *
 * Docked rather than floating: the preview drawer keeps its actions along the bottom
 * edge, where a floating launcher would sit on top of "Open full record".
 */
const OntologyAssistantLayout: React.FC = () => {
    const [open, setOpen] = useState(false);
    const [interactionId, setInteractionId] = useState(generateInteractionId);

    const toggle = useCallback(() => setOpen(prev => !prev), []);
    const panel = useMemo(() => ({ label: 'Knowledge Assistant', open, toggle }), [open, toggle]);

    return (
        <AssistantPanelContext.Provider value={panel}>
            <div className="flex h-screen overflow-hidden bg-page">
                <div className="flex-1 min-w-0">
                    <Outlet />
                </div>

                {/* Hidden rather than unmounted when closed, so reopening keeps the thread. */}
                <aside
                    aria-label="Knowledge Assistant"
                    className="flex-col border-l border-line bg-surface"
                    style={{ width: PANEL_WIDTH, flexShrink: 0, display: open ? 'flex' : 'none' }}
                >
                    <div className="h-16 px-4 flex items-center justify-between border-b border-line bg-page/50">
                        <div>
                            <p className="m-0 text-xs font-bold text-fg" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                                Knowledge Assistant
                            </p>
                            <p className="m-0 mt-0.5 text-[10px] text-fg-muted" style={{ fontFamily: '"Inter", sans-serif' }}>
                                AI Powered • Policy &amp; Risk Library
                            </p>
                        </div>
                        <div className="flex items-center">
                            <Tooltip title="New conversation">
                                <IconButton
                                    size="small"
                                    onClick={() => setInteractionId(generateInteractionId())}
                                    sx={{ color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg') } }}
                                >
                                    <RestartAltIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Close">
                                <IconButton
                                    size="small"
                                    onClick={() => setOpen(false)}
                                    sx={{ color: dsVar('fg-2'), '&:hover': { color: dsVar('accent-fg') } }}
                                >
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="flex-1 min-h-0">
                        {/* Keyed on the session so "New conversation" also clears the thread. */}
                        <ChatAssistance
                            key={interactionId}
                            agentCode={AGENT_CODE}
                            interactionId={interactionId}
                            showReasoning
                            placeholder="Ask about policies, parameters, thresholds..."
                            initialMessage="Hi! I'm your Knowledge Assistant. Ask me about the policies, risk concepts, parameters and thresholds in the Policy & Risk Library — for example, *what thresholds are in place for anomaly detection?*"
                        />
                    </div>
                </aside>
            </div>
        </AssistantPanelContext.Provider>
    );
};

export default OntologyAssistantLayout;
